/**
 * Arudha padas — the perceived or public face of each bhava.
 *
 * For a house, count the distance to its lord, then count that same distance
 * again onward from the lord. If the resulting pada falls in the house itself
 * or the 7th from it — where the image would coincide with the thing itself —
 * it is moved 10 houses on.
 *
 * This reproduces the standard table for the Lagna lord's placement:
 *   lord in  1  2  3  4  5  6  7  8  9 10 11 12
 *   Arudha  10  3  5  4  9 11 10  3  5  4  9 11
 * and explains why the Arudha can never fall in the 1st, 2nd, 6th, 7th, 8th
 * or 12th from its own house.
 *
 * A1 is the Arudha Lagna (public image and reputation); A12 is the Upapada
 * (Upapada Lagna), traditionally read for marriage and partnership.
 */

const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

const SIGN_RULERS = {
  Aries: 'Mars', Taurus: 'Venus', Gemini: 'Mercury', Cancer: 'Moon',
  Leo: 'Sun', Virgo: 'Mercury', Libra: 'Venus', Scorpio: 'Mars',
  Sagittarius: 'Jupiter', Capricorn: 'Saturn', Aquarius: 'Saturn',
  Pisces: 'Jupiter',
};

/** Traditional name of each bhava's arudha. */
export const ARUDHA_NAMES = {
  A1: 'Arudha Lagna (Tanu Pada)',
  A2: 'Kosha Pada',
  A3: 'Vikrama Pada',
  A4: 'Matru Pada',
  A5: 'Putra Pada',
  A6: 'Shatru Pada',
  A7: 'Jamitra Pada',
  A8: 'Aayu Pada',
  A9: 'Bhagya Pada',
  A10: 'Rajya Pada',
  A11: 'Labha Pada',
  A12: 'Upapada (Upapada Lagna)',
};

/** What each arudha is read for, in plain language. */
export const ARUDHA_MEANINGS = {
  A1: 'public image and reputation',
  A2: 'perceived wealth',
  A3: 'perceived courage and initiative',
  A4: 'perceived home and comfort',
  A5: 'perceived creativity and children',
  A6: 'perceived rivals and obstacles',
  A7: 'perceived partnerships',
  A8: 'perceived longevity and upheaval',
  A9: 'perceived fortune and beliefs',
  A10: 'perceived career and status',
  A11: 'perceived gains and networks',
  A12: 'marriage and partnership (Upapada)',
};

/**
 * Sign of the arudha pada for one house.
 * @param {number} houseNum 1-12, counted from the Lagna
 * @returns {{house, sign, lord, lordSign, lordHouse, relativeHouse}|null}
 */
function arudhaFor(houseNum, lagnaSign, planets) {
  const lagnaIndex = SIGNS.indexOf(lagnaSign);
  if (lagnaIndex < 0) return null;

  const houseSign = SIGNS[(lagnaIndex + houseNum - 1) % 12];
  const lord = SIGN_RULERS[houseSign];
  const lordSign = planets?.[lord]?.sign;
  if (!SIGNS.includes(lordSign)) return null;

  const houseIndex = SIGNS.indexOf(houseSign);
  const lordIndex = SIGNS.indexOf(lordSign);

  // Distance from the house to its lord, counting the house as 1.
  const distance = ((lordIndex - houseIndex + 12) % 12) + 1;
  // The same distance again, onward from the lord.
  let padaIndex = (lordIndex + distance - 1) % 12;

  // The image cannot sit on the thing itself or directly opposite it.
  const relative = ((padaIndex - houseIndex + 12) % 12) + 1;
  if (relative === 1 || relative === 7) {
    padaIndex = (padaIndex + 10 - 1) % 12;
  }

  return {
    house: houseNum,
    sign: SIGNS[padaIndex],
    lord,
    lordSign,
    lordHouse: ((lordIndex - lagnaIndex + 12) % 12) + 1,
    relativeHouse: ((padaIndex - lagnaIndex + 12) % 12) + 1,
  };
}

/**
 * All twelve arudha padas, keyed A1..A12.
 * @returns {Object|null} null when the Lagna is unknown.
 */
export function resolveArudhas(chartData) {
  const lagnaSign = chartData?.houses?.ascendant?.sign;
  const planets = chartData?.planets;
  if (!SIGNS.includes(lagnaSign) || !planets) return null;

  const out = {};
  for (let h = 1; h <= 12; h += 1) {
    const a = arudhaFor(h, lagnaSign, planets);
    if (a) out[`A${h}`] = a;
  }
  return Object.keys(out).length ? out : null;
}

export default resolveArudhas;
